import {
  confirmMessagesOf,
} from '@llj/ai-service-interface/dist/entities/tool-catalogue.interface'
import { toolFailure } from '../../core/result'
import type { ToolErrorCode } from '../../core/result'
import { TOOL_ERROR } from '../../core/errors'

// 確認類工具的結果形狀（#1693）。🔴 只依賴純資料的工具目錄與 core 的葉子模組：略過端點與前端都用它，
// 載到 ai SDK 或 gRPC 會讓那兩條路載不起來。

// 流程層錯誤碼：框架層的 TOOL_* 取自 core/errors，這裡只放確認流程自己的
const CONFIRM_ERROR = {
  CONFIRM_ONCE_PER_STEP: 'CONFIRM_ONCE_PER_STEP',
  PROPOSED_ARGS_INVALID: 'PROPOSED_ARGS_INVALID',
} as const

// 確認卡走到哪一步。awaiting／executing 只在畫面上出現，其餘四種也是寫給模型的結果
type ConfirmStatus =
  'awaiting_confirmation' | 'executing' | 'executed' | 'failed' | 'cancelled' | 'interrupted'

// prepare 提出、等使用者確認時，串流裡那一筆的 output。模型看不到它：loop 在這一步停住
interface ConfirmAwaitingOutput {
  status: 'awaiting_confirmation'
  proposedArgs: unknown
  display: unknown
}

const isConfirmAwaitingOutput = (value: unknown): value is ConfirmAwaitingOutput =>
  typeof value === 'object' && value !== null &&
  (value as { status?: unknown }).status === 'awaiting_confirmation'

// 寫回事件流、模型續跑時讀到的結果。失敗帶 error 字串，事件投影因此判成 failed
// 成功時 message 放在 result 前面，模型先讀到「已確認、已完成」再看結果（#1703）
const executedResult = (toolName: string, result: unknown) =>
  ({ status: 'executed' as const, message: confirmMessagesOf(toolName).executed, result })

const failedResult = (error: ToolErrorCode, message: string) =>
  ({ status: 'failed' as const, error, message })

type AbandonReason = 'cancelled' | 'interrupted'

// 放棄確認時寫給模型的結果，字句由工具目錄提供。略過端點與前端共用，兩邊一字不差
const abandonedResult = (toolName: string, reason: AbandonReason) =>
  ({ status: reason, message: confirmMessagesOf(toolName)[reason] })

// 確認時重新授權沒過（#1693）：權限、模組或開關在出卡之後被收回。不執行，模型負責說明
const NOT_AUTHORIZED_RESULT = failedResult(
  TOOL_ERROR.TOOL_NOT_AUTHORIZED,
  '使用者目前已沒有這項操作的權限（權限、模組或功能開關在出卡後有變動），沒有執行。',
)

// 存下來的參數不符工具的白名單（#1693）：不執行，請模型重新出卡
const PROPOSED_ARGS_INVALID_RESULT = failedResult(
  CONFIRM_ERROR.PROPOSED_ARGS_INVALID, '確認卡上的參數格式不正確，沒有執行。請重新整理參數後再出一次確認卡。',
)

// 註冊表裡找不到這支工具的執行函式（例如已下架）：不執行
const TOOL_NOT_REGISTERED_RESULT = failedResult(
  TOOL_ERROR.TOOL_NOT_REGISTERED, '這項操作目前無法執行（工具未註冊），沒有執行。',
)

// 執行逾時：呼叫可能仍在背後完成，所以要使用者先查證，不能直接重試
const EXECUTE_TIMEOUT_RESULT = failedResult(
  TOOL_ERROR.TOOL_TIMEOUT, '執行逾時，無法確認是否已完成。請使用者先到後台查看結果，不要直接重試。',
)

// 執行函式丟出例外：不知道細節也不重試，訊息不帶內部內容
const EXECUTE_THREW_RESULT = failedResult(
  TOOL_ERROR.TOOL_FAILED, '執行時發生錯誤，沒有完成。請告訴使用者稍後再試。',
)

// 同一步只有一份暫停：多出來的確認記成失敗，對話才不會同時卡著兩張卡
const CONFIRM_ONCE_PER_STEP_RESULT = toolFailure(
  CONFIRM_ERROR.CONFIRM_ONCE_PER_STEP,
  '同一步只能送出一張確認卡，這一張沒有送給使用者；請等使用者處理完再呼叫。',
)

export {
  CONFIRM_ERROR,
  CONFIRM_ONCE_PER_STEP_RESULT,
  EXECUTE_THREW_RESULT,
  EXECUTE_TIMEOUT_RESULT,
  NOT_AUTHORIZED_RESULT,
  PROPOSED_ARGS_INVALID_RESULT,
  TOOL_NOT_REGISTERED_RESULT,
  abandonedResult,
  executedResult,
  failedResult,
  isConfirmAwaitingOutput,
}
export type { AbandonReason, ConfirmAwaitingOutput, ConfirmStatus }
